import React, { useEffect, useState } from "react";
import style from "./SubmissionDetail.module.css";

/**
 * Small dark-themed confirmation dialog (replaces window.confirm).
 * Optionally shows an "email the creator" checkbox and a "trust this creator"
 * checkbox; onConfirm receives { notify, trustCreator }.
 *
 * props:
 *  open, title, message, confirmLabel, tone ('success'|'danger'),
 *  submitting, showNotify, showTrustCreator, trustCreatorLabel,
 *  trustCreatorHelper, onCancel, onConfirm({ notify, trustCreator })
 */
const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  tone = "success",
  submitting = false,
  showNotify = false,
  // Opt-in: "never ask again for this creator". Hidden by default so every
  // other caller of this dialog is unaffected.
  showTrustCreator = false,
  trustCreatorLabel = "",
  trustCreatorHelper = "",
  onCancel,
  onConfirm,
  // Optional extra classes merged onto the title/buttons, for callers that
  // need to restyle this dialog without touching the shared CSS module.
  titleClassName = "",
  cancelClassName = "",
  confirmClassName = "",
}) => {
  const [notify, setNotify] = useState(true);
  // Defaults to OFF: granting trust must be a deliberate tick, never something
  // an admin does by accident while clicking through approvals.
  const [trustCreator, setTrustCreator] = useState(false);

  useEffect(() => {
    if (open) {
      setNotify(true);
      setTrustCreator(false);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className={style.modalOverlay} onClick={submitting ? undefined : onCancel}>
      <div className={style.modal} onClick={(e) => e.stopPropagation()}>
        <h3 className={`${style.modalTitle} ${titleClassName}`}>{title}</h3>
        {message && <p className={style.modalSub}>{message}</p>}

        {showNotify && (
          <label className={style.notifyRow}>
            <input
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              disabled={submitting}
            />
            Email the creator about this decision
          </label>
        )}

        {showTrustCreator && (
          <div className={style.trustRow}>
            <label className={style.notifyRow}>
              <input
                type="checkbox"
                checked={trustCreator}
                onChange={(e) => setTrustCreator(e.target.checked)}
                disabled={submitting}
              />
              {trustCreatorLabel}
            </label>
            {trustCreatorHelper && (
              <p className={style.trustHelper}>{trustCreatorHelper}</p>
            )}
          </div>
        )}

        <div className={style.modalActions}>
          <button
            className={`${style.modalCancel} ${cancelClassName}`}
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className={`${
              tone === "danger" ? style.modalReject : style.modalConfirm
            } ${confirmClassName}`}
            onClick={() =>
              onConfirm({
                notify: showNotify ? notify : false,
                trustCreator: showTrustCreator ? trustCreator : false,
              })
            }
            disabled={submitting}
          >
            {submitting ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
